import { useState, useCallback } from "react";
import type { FormFoodItem, FormGalleryItem, RestaurantFormData } from "../../restaurant.types";
import { Utensils, ImageIcon, Plus, QrCode, AlertCircle, CheckCircle2, Loader2, X, Camera, UtensilsCrossed, Wind } from "lucide-react";
import { removeRestaurantFile, uploadFile } from "../../../../lib/api/restaurant.apis";
import MenuBuilderModal from "./MenuBuilderModal";

interface GalleryProps {
  restaurantId: string;
  restaurantName: string;
  publishedPdfUrl: string | null;
  formData: RestaurantFormData;
  updateFormData: (updatedFields: Partial<RestaurantFormData>) => void;
  setFormData: React.Dispatch<React.SetStateAction<RestaurantFormData>>;
}

const categoryConfig = [
  {
    id: "cover",
    label: "Cover Photo",
    sublabel: "Primary listing image",
    icon: Camera,
    description: "The first impression of your restaurant choose a high-quality, welcoming shot.",
    accept: ".jpg,.jpeg,.png,.webp,.heic,.heif",
    multiple: false,
  },
  {
    id: "menu",
    label: "Menu",
    sublabel: "Menus & price lists",
    icon: UtensilsCrossed,
    description: "Upload clear photos of your menus.",
    accept: ".jpg,.jpeg,.png,.webp,.heic,.heif",
    multiple: true,
  },
  {
    id: "food",
    label: "Signature Dishes",
    sublabel: "Food photography",
    icon: Utensils,
    description: "Showcase your best dishes. High-quality food photography drives orders.",
    accept: ".jpg,.jpeg,.png,.webp,.heic,.heif",
    multiple: true,
  },
  {
    id: "ambiance",
    label: "Ambiance",
    sublabel: "Interior & atmosphere",
    icon: Wind,
    description: "Let guests feel the atmosphere before they visit.",
    accept: ".jpg,.jpeg,.png,.webp,.heic,.heif",
    multiple: true,
  },
];

/* ─── tiny shared helpers ─── */
function isCloudImage(item: FormGalleryItem): boolean {
  return typeof item.url === "string" && item.url.includes("res.cloudinary.com");
}

function getPreviewSrc(input?: File | string | null): string | undefined {
  if (!input) return undefined;
  if (typeof input === "string") return input;
  if (input instanceof File) return URL.createObjectURL(input);
  return undefined;
}

/* ─── Status badge ─── */
const StatusBadge = ({ status }: { status: string }) => {
  if (status === "uploading")
    return (
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl z-10">
        <Loader2 className="w-5 h-5 text-[#FFD166] animate-spin mb-1.5" />
        <span className="text-white text-[10px] font-medium tracking-wide uppercase">Uploading</span>
      </div>
    );
  if (status === "error")
    return (
      <div className="absolute inset-0 bg-red-900/70 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl z-10">
        <AlertCircle className="w-5 h-5 text-red-300 mb-1" />
        <span className="text-red-200 text-[10px] font-medium tracking-wide uppercase">Failed</span>
      </div>
    );
  if (status === "uploaded")
    return (
      <div className="absolute top-2 left-2 bg-emerald-500/90 backdrop-blur-sm rounded-full px-2 py-0.5 flex items-center gap-1 z-10">
        <CheckCircle2 className="w-3 h-3 text-white" />
        <span className="text-white text-[9px] font-semibold tracking-wider uppercase">Uploaded</span>
      </div>
    );
  return null;
};

/* ─── Single photo card ─── */
const PhotoCard = ({
  item,
  category,
  index,
  onRemove,
  onLabelChange,
  isDeleting,
}: {
  item: FormGalleryItem | FormFoodItem;
  category: string;
  index: number;
  onRemove: () => void;
  onLabelChange?: (id: string, value: string) => void;
  isDeleting: boolean;
}) => {
  const src = getPreviewSrc(item.url);

  return (
    <div
      className="group relative flex flex-col gap-1.5"
      style={{ animation: "fadeSlideUp 0.25s ease both" }}
    >
      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 shadow-sm ring-1 ring-gray-200 transition-shadow group-hover:shadow-md group-hover:ring-[#FFD166]/50">
        {src ? (
          <img src={src} alt={category} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}

        <StatusBadge status={item.status} />

        {/* Delete button */}
        <button
          onClick={onRemove}
          disabled={isDeleting || item.status === "uploading"}
          aria-label="Remove photo"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center z-20
            opacity-0 group-hover:opacity-100 transition-all duration-200
            hover:bg-red-500 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isDeleting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <X className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {/* Food name input */}
      {category === "food" && onLabelChange && (
        <input
          type="text"
          value={(item as FormFoodItem).label || ""}
          onChange={(e) => onLabelChange(item.id, e.target.value)}
          placeholder="Dish name (optional)"
          className="w-full px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-[#FFD166]/60 focus:border-[#FFD166]
            placeholder:text-gray-400 transition-all"
        />
      )}
    </div>
  );
};

/* ─── Add-more tile ─── */
const AddTile = ({ category, onFiles }: { category: string; onFiles: (files: FileList) => void }) => (
  <label
    className="relative w-full aspect-[4/3] rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center
      hover:border-[#FFD166] hover:bg-amber-50/50 transition-all duration-200 cursor-pointer group"
  >
    <div className="w-9 h-9 rounded-full bg-gray-100 group-hover:bg-[#FFD166]/20 flex items-center justify-center mb-2 transition-colors">
      <Plus className="w-4.5 h-4.5 text-gray-400 group-hover:text-[#FFD166] transition-colors" />
    </div>
    <span className="text-xs font-medium text-gray-400 group-hover:text-[#FFD166] transition-colors">Add photo</span>
    <input
      type="file"
      multiple={category !== "cover"}
      accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
      onChange={(e) => e.target.files && e.target.files.length > 0 && onFiles(e.target.files)}
      className="hidden"
    />
  </label>
);

/* ─── Empty-state upload zone ─── */
const EmptyUploadZone = ({
  category,
  icon: Icon,
  description,
  accept,
  multiple,
  onFiles,
}: {
  category: string;
  icon: React.ElementType;
  description: string;
  accept: string;
  multiple: boolean;
  onFiles: (files: FileList) => void;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files);
    },
    [onFiles]
  );

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`flex flex-col items-center justify-center w-full py-10 px-6 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200
        ${isDragging
          ? "border-[#FFD166] bg-amber-50 scale-[1.01]"
          : "border-gray-200 bg-gray-50/50 hover:border-[#FFD166]/60 hover:bg-amber-50/30"
        }`}
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 transition-colors
        ${isDragging ? "bg-[#FFD166]/30" : "bg-gray-100"}`}
      >
        <Icon className={`w-6 h-6 transition-colors ${isDragging ? "text-amber-600" : "text-gray-400"}`} />
      </div>
      <p className="text-sm font-semibold text-gray-700 mb-0.5">
        {isDragging ? "Drop to upload" : "Drag & drop or click to upload"}
      </p>
      <p className="text-xs text-gray-500 text-center max-w-xs">{description}</p>
      <div className="mt-4 px-4 py-2 rounded-lg bg-white border border-gray-200 shadow-sm text-xs font-semibold text-gray-600 hover:border-[#FFD166] transition-colors">
        Browse files
      </div>
      <input type="file" multiple={multiple} accept={accept}
        onChange={(e) => e.target.files && e.target.files.length > 0 && onFiles(e.target.files)}
        className="hidden"
      />
    </label>
  );
};

/* ══════════════════════════════════════════
   Main component
══════════════════════════════════════════ */
const RenderGallery: React.FC<GalleryProps> = ({
  restaurantId,
  restaurantName,
  publishedPdfUrl,
  formData,
  updateFormData,
  setFormData,
}) => {
  const [uploadingItems, setUploadingItems] = useState<Set<string>>(new Set());
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const [showMenuBuilder, setShowMenuBuilder] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  /* ─── Upload handler ─── */
  const handleUploadItem = async (
    item: FormGalleryItem | FormFoodItem,
    category: "cover" | "menu" | "food" | "ambiance"
  ) => {
    const itemId = item.id;
    setUploadingItems((prev) => new Set(prev).add(itemId));
    try {
      const result = await uploadFile(item.url as File);
      setFormData((prev) => {
        if (category === "food" || category === "ambiance" || category === "menu") {
          const updated = (prev.gallery[category] || []).map((i) =>
            i.id === itemId
              ? { ...i, status: "uploaded", url: result.url, publicId: result.publicId, progress: 100 }
              : i
          );
          return { ...prev, gallery: { ...prev.gallery, [category]: updated } };
        }
        return {
          ...prev,
          gallery: {
            ...prev.gallery,
            [category]: { ...item, status: "uploaded", url: result.url, publicId: result.publicId, progress: 100 },
          },
        };
      });
    } catch {
      setFormData((prev) => {
        if (category === "food" || category === "ambiance" || category === "menu") {
          const updated = (prev.gallery[category] || []).map((i) =>
            i.id === itemId ? { ...i, status: "error", progress: 0 } : i
          );
          return { ...prev, gallery: { ...prev.gallery, [category]: updated } };
        }
        return { ...prev, gallery: { ...prev.gallery, [category]: { ...item, status: "error", progress: 0 } } };
      });
    } finally {
      setUploadingItems((prev) => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  };

  const handleFileUpload = (
    category: "cover" | "food" | "ambiance" | "menu",
    files: FileList
  ) => {
    const fileArray = Array.from(files);
    if (category === "food") {
      const newItems: FormFoodItem[] = fileArray.map((file) => ({
        url: file, label: "", id: Math.random().toString(36).substr(2, 9), status: "uploading", progress: 0,
      }));
      updateFormData({ gallery: { ...formData.gallery, food: [...(formData.gallery.food || []), ...newItems] } });
      newItems.forEach((i) => handleUploadItem(i, "food"));
    } else if (category === "ambiance" || category === "menu") {
      const newItems: FormGalleryItem[] = fileArray.map((file) => ({
        id: Math.random().toString(36).substr(2, 9), url: file, status: "uploading", progress: 0,
      }));
      updateFormData({ gallery: { ...formData.gallery, [category]: [...(formData.gallery[category] || []), ...newItems] } });
      newItems.forEach((i) => handleUploadItem(i, category));
    } else {
      const newItem: FormGalleryItem = {
        id: Math.random().toString(36).substr(2, 9), url: fileArray[0], status: "uploading", progress: 0,
      };
      updateFormData({ gallery: { ...formData.gallery, cover: newItem } });
      handleUploadItem(newItem, "cover");
    }
  };

  const removeFile = async (
    category: "cover" | "food" | "ambiance" | "menu",
    index?: number,
    foodId?: string
  ) => {
    const gallery = formData.gallery;
    if (category === "cover") {
      const item = gallery.cover;
      if (!item) return;
      if (item.url instanceof File) {
        updateFormData({ gallery: { ...gallery, cover: null } });
      } else if (isCloudImage(item)) {
        await removeRestaurantFile(formData.id!, category);
        updateFormData({ gallery: { ...gallery, cover: null } });
      }
    } else if (category === "food" && foodId) {
      const items = gallery.food || [];
      const target = items.find((f) => f.id === foodId);
      if (!target) return;
      if (target.url instanceof File) {
        updateFormData({ gallery: { ...gallery, food: items.filter((f) => f.id !== foodId) } });
      } else if (isCloudImage(target)) {
        await removeRestaurantFile(formData.id!, category, undefined, foodId);
        updateFormData({ gallery: { ...gallery, food: items.filter((f) => f.id !== foodId) } });
      }
    } else if ((category === "ambiance" || category === "menu") && index !== undefined) {
      const items = gallery[category] || [];
      const target = items[index];
      if (!target) return;
      const newItems = items.filter((_, i) => i !== index);
      if (target.url instanceof File) {
        updateFormData({ gallery: { ...gallery, [category]: newItems } });
      } else if (isCloudImage(target)) {
        await removeRestaurantFile(formData.id!, category, index);
        updateFormData({ gallery: { ...gallery, [category]: newItems } });
      }
    }
  };

  const updateFoodName = (foodId: string, name: string) => {
    updateFormData({
      gallery: {
        ...formData.gallery,
        food: (formData.gallery.food || []).map((i) => (i.id === foodId ? { ...i, label: name } : i)),
      },
    });
  };

  const cloudMenuImages = (formData.gallery.menu || [])
    .filter((img) => typeof img.url === "string")
    .map((img) => ({ id: img.id, url: img.url as string }));

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">Photos & Gallery</h3>
            <p className="text-sm text-gray-500 mt-0.5">High-quality visuals significantly increase customer engagement</p>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <ImageIcon className="w-3.5 h-3.5" />
            <span>JPEG, PNG supported</span>
          </div>
        </div>

        {/* Section cards */}
        <div className="space-y-3">
          {categoryConfig.map((cat) => {
            const Icon = cat.icon;
            const raw = formData.gallery[cat.id as keyof typeof formData.gallery];
            const itemArray: (FormGalleryItem | FormFoodItem)[] = Array.isArray(raw)
              ? (raw as any[]).filter(Boolean)
              : raw
              ? [raw as FormGalleryItem]
              : [];

            const isOpen = activeSection === cat.id || itemArray.length > 0;
            const uploadedCount = itemArray.filter((i) => i.status === "uploaded").length;
            const pendingCount = itemArray.filter((i) => i.status === "uploading").length;
            const errorCount = itemArray.filter((i) => i.status === "error").length;

            return (
              <div
                key={cat.id}
                className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Section header */}
                <button
                  type="button"
                  onClick={() => setActiveSection(isOpen && activeSection === cat.id ? null : cat.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50/70 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-amber-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{cat.label}</span>
                      <span className="text-xs text-gray-400">{cat.sublabel}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {itemArray.length === 0 ? (
                        <span className="text-xs text-gray-400">No files uploaded yet</span>
                      ) : (
                        <>
                          {uploadedCount > 0 && (
                            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />{uploadedCount} saved
                            </span>
                          )}
                          {pendingCount > 0 && (
                            <span className="text-xs text-amber-600 font-medium flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />{pendingCount} uploading
                            </span>
                          )}
                          {errorCount > 0 && (
                            <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />{errorCount} failed
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Right-side actions */}
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {cat.id === "menu" && cloudMenuImages.length > 0 && (
                      <button
                        onClick={() => setShowMenuBuilder(true)}
                        className="flex items-center gap-1.5 bg-[#FFD166] hover:bg-amber-400 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-900 transition-colors shadow-sm"
                      >
                        <QrCode className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Generate QR</span>
                        <span className="sm:hidden">QR</span>
                      </button>
                    )}
                    <div className={`w-5 h-5 rounded-full border border-gray-200 flex items-center justify-center transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                      <svg className="w-3 h-3 text-gray-400" viewBox="0 0 12 12" fill="none">
                        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Section body */}
                {isOpen && (
                  <div className="px-5 pb-5 pt-1 border-t border-gray-100">
                    {itemArray.length === 0 ? (
                      <EmptyUploadZone
                        category={cat.id}
                        icon={Icon}
                        description={cat.description}
                        accept={cat.accept}
                        multiple={cat.multiple}
                        onFiles={(files) => handleFileUpload(cat.id as any, files)}
                      />
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                          {itemArray.map((item, index) => {
                            if (!item.url) return null;
                            const isDeleting = deletingItems.has(item.id);

                            return (
                              <PhotoCard
                                key={item.id}
                                item={item}
                                category={cat.id}
                                index={index}
                                isDeleting={isDeleting}
                                onLabelChange={cat.id === "food" ? updateFoodName : undefined}
                                onRemove={async () => {
                                  setDeletingItems((prev) => new Set(prev).add(item.id));
                                  try {
                                    await removeFile(cat.id as any, index, item.id);
                                  } finally {
                                    setDeletingItems((prev) => { const s = new Set(prev); s.delete(item.id); return s; });
                                  }
                                }}
                              />
                            );
                          })}
                          {/* Add more tile — only show for multi-upload sections or cover without an image */}
                          {(cat.multiple || itemArray.length === 0) && (
                            <AddTile
                              category={cat.id}
                              onFiles={(files) => handleFileUpload(cat.id as any, files)}
                            />
                          )}
                        </div>

                        {/* Error retry hint */}
                        {errorCount > 0 && (
                          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-600">
                              {errorCount} {errorCount === 1 ? "file" : "files"} failed to upload. Remove and try again, or check your connection.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Menu Builder Modal */}
      {showMenuBuilder && (
        <MenuBuilderModal
          restaurantId={restaurantId}
          restaurantName={restaurantName}
          galleryMenuImages={cloudMenuImages}
          publishedPdfUrl={publishedPdfUrl}
          onClose={() => setShowMenuBuilder(false)}
          onPublishSuccess={() => {}}
        />
      )}
    </>
  );
};

export default RenderGallery;